import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MediaService from "../../../services/KangarooServices/MediaServices";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { imageBaseApi } from "../../../utilis/imageHelper";
import MediaModal from "../../PagesSection/Component/MediaModal";
import EventService from "../../../services/KangarooServices/EventService";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import Select from "react-select";
import * as Yup from "yup";

const formatTime = (date) => {
  if (!date) return "";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [destinationData, setDestinationData] = useState([]);
  const [mediaData, setMediaData] = useState([]);
  const [detailData, setDetailData] = useState({});
  console.log("🚀 ~ EventForm ~ detailData:", detailData);

  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaSelectionType, setMediaSelectionType] = useState(""); // "banner", "thumbnail", or city index
  const [selectedBannerMedia, setSelectedBannerMedia] = useState(null);
  const [selectedThumbnailMedia, setSelectedThumbnailMedia] = useState(null);
  const [selectedCityMedia, setSelectedCityMedia] = useState([]); // Array for each city's image
  const [activeTab, setActiveTab] = useState("1");
  const [activeMainTab, setActiveMainTab] = useState("1");

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);

  // Fetch functions
  const fetchMedia = async () => {
    try {
      const response = await MediaService.getList({ page: 1, pageSize: 1000 });
      setMediaData(response.data || response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDestination = async () => {
    try {
      const response = await DestinationService.getList();
      setDestinationData(response.data || response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEventDetail = async () => {
    try {
      const response = await EventService.getBySlug(id);
      setDetailData(response);

      setSelectedBannerMedia(response.bannerMedia || null);
      setSelectedThumbnailMedia(response.thumbnailMedia || null);

      // Initialize city media array
      if (response.eventsCities?.length > 0) {
        setSelectedCityMedia(
          response.eventsCities.map((city) => city.eventImage || null)
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDestination();
    fetchMedia();
    if (id) fetchEventDetail();
  }, [id]);

  // Options
  const destinationOptions = destinationData.map((dest) => ({
    value: dest.id,
    label: dest.name,
  }));

  // Validation Schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Event name is required"),
    description: Yup.string().required("Description is required"),
    targetCountryId: Yup.string().required("Target country is required"),
    eventsCities: Yup.array()
      .of(
        Yup.object({
          city: Yup.string().required("City is required"),
          eventDate: Yup.date().required("Event date is required"),
          timeRange: Yup.string().required("Time range is required"),
          location: Yup.string().required("Location is required"),
          phoneNumber: Yup.string().required("Phone number is required"),
        })
      )
      .min(1, "At least one city is required"),
    registrationUrl: Yup.string()
      .url("Invalid URL")
      .when("registrationRequired", {
        is: true,
        then: (schema) => schema.required("Registration URL is required"),
      }),
  });

  const formik = useFormik({
    initialValues: {
      id: detailData?.id || null,
      name: detailData?.name || "",
      description: detailData?.description || "",
      eventType: detailData?.eventType || "",
      startDate: detailData?.startDate || "",
      endDate: detailData?.endDate || "",
      venue: detailData?.venue || "",
      speaker: detailData?.speaker || "",
      targetCountryId: detailData?.targetCountry?.id || "",
      registrationRequired: detailData?.registrationRequired ?? false,
      registrationUrl: detailData?.registrationUrl || null,
      isActive: detailData?.isActive ?? true,
      eventsCities:
        detailData?.eventsCities?.length > 0
          ? detailData.eventsCities.map((city) => ({
              id: city.id || null,
              city: city.city || "",
              eventDate: city.eventDate || "",
              timeRange: city.timeRange || "", // store entire range here
              location: city.location || "",
              phoneNumber: city.phoneNumber || "",
              eventImageId: city.eventImageId || "",
            }))
          : [
              {
                city: "",
                eventDate: "",
                timeRange: "",
                location: "",
                phoneNumber: "",
                eventImageId: "",
              },
            ],
    },
    enableReinitialize: true,

    onSubmit: async (values) => {
      try {
        const payload = {
          id: values?.id,
          name: values.name,
          description: values.description,
          eventType: values.eventType || null,
          isActive: values?.isActive,
          startDate: values.startDate
            ? new Date(values.startDate).toISOString()
            : null,
          endDate: values.endDate
            ? new Date(values.endDate).toISOString()
            : null,
          venue: values.venue || null,
          speaker: values.speaker || null,
          bannerMediaId: selectedBannerMedia?.id || null,
          thumbnailMediaId: selectedThumbnailMedia?.id || null,
          targetCountryId: values.targetCountryId || null,
          registrationRequired: values.registrationRequired || false,
          registrationUrl: values.registrationRequired
            ? values.registrationUrl
            : null,
          eventsCities: values.eventsCities.map((city, index) => ({
            ...city,
            eventDate: city.eventDate
              ? new Date(city.eventDate).toISOString()
              : null,
            eventImageId: selectedCityMedia[index]?.id || null,
          })),
        };

        if (id) {
          await EventService.update(payload);
          toast.success("Event updated successfully");
        } else {
          await EventService.create(payload);
          toast.success("Event created successfully");
        }

        navigate("/event");
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong!");
      }
    },
  });

  // Media Selection Handlers
  const openMediaModal = (typeOrIndex) => {
    setMediaSelectionType(typeOrIndex);
    setIsMediaOpen(true);
  };

  const handleMediaSelect = (media) => {
    if (mediaSelectionType === "banner") {
      setSelectedBannerMedia(media);
    } else if (mediaSelectionType === "thumbnail") {
      setSelectedThumbnailMedia(media);
    } else if (typeof mediaSelectionType === "number") {
      const newCityMedia = [...selectedCityMedia];
      newCityMedia[mediaSelectionType] = media;
      setSelectedCityMedia(newCityMedia);
    }
    setIsMediaOpen(false);
    setMediaSelectionType("");
  };

  // City Management
  const addCity = () => {
    formik.setFieldValue("eventsCities", [
      ...formik.values.eventsCities,
      {
        id: "",
        city: "",
        eventDate: "",
        timeRange: "",
        location: "",
        phoneNumber: "",
        eventImageId: "",
      },
    ]);
    setSelectedCityMedia([...selectedCityMedia, null]);
    setActiveTab(formik.values.eventsCities.length);
  };

  const removeCity = (index) => {
    if (formik.values.eventsCities.length <= 1) {
      toast.warn("At least one city is required");
      return;
    }
    const newCities = formik.values.eventsCities.filter((_, i) => i !== index);
    const newMedia = selectedCityMedia.filter((_, i) => i !== index);
    formik.setFieldValue("eventsCities", newCities);
    setSelectedCityMedia(newMedia);
    if (activeTab >= newCities.length) setActiveTab(newCities.length - 1);
  };

  const breadcrumbItems = [{ title: "Back to List", link: "/event" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Event Form"
          pageTitle="Events"
          breadcrumbItems={breadcrumbItems}
        />
        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button type="submit" className="btn btn-success">
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/event")}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <Nav tabs className="card-header-tabs">
                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeMainTab === "1" ? "active" : ""}
                        onClick={() => setActiveMainTab("1")}
                      >
                        Basic Info
                      </NavLink>
                    </NavItem>

                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeMainTab === "2" ? "active" : ""}
                        onClick={() => setActiveMainTab("2")}
                      >
                        City
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <CardBody>
                  <TabContent activeTab={activeMainTab}>
                    <TabPane tabId="1">
                      <Card>
                        <CardBody>
                          {/* Name */}
                          <div className="mb-3">
                            <Label>
                              Name <span className="text-danger">*</span>
                            </Label>
                            <Input
                              name="name"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.name && !!formik.errors.name
                              }
                            />
                            {formik.touched.name && formik.errors.name && (
                              <div className="text-danger mt-1">
                                {formik.errors.name}
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          <div className="mb-3">
                            <Label>
                              Description <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="textarea"
                              rows={6}
                              name="description"
                              value={formik.values.description}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched.description &&
                                !!formik.errors.description
                              }
                            />
                            {formik.touched.description &&
                              formik.errors.description && (
                                <div className="text-danger mt-1">
                                  {formik.errors.description}
                                </div>
                              )}
                          </div>

                          {/* Event Type */}
                          <div className="mb-3">
                            <Label>Event Type</Label>
                            <Input
                              name="eventType"
                              value={formik.values.eventType}
                              onChange={formik.handleChange}
                            />
                          </div>

                          {/* Date Range */}
                          <div className="mb-3">
                            <Label>Event Duration</Label>
                            <Flatpickr
                              className="form-control"
                              options={{ mode: "range", dateFormat: "Y-m-d" }}
                              value={[
                                formik.values.startDate,
                                formik.values.endDate,
                              ]}
                              onChange={([start, end]) => {
                                formik.setFieldValue("startDate", start || "");
                                formik.setFieldValue("endDate", end || "");
                              }}
                            />
                          </div>
                          <div className="mb-3 d-flex align-items-center gap-2">
                            <Input
                              type="checkbox"
                              checked={formik.values.isActive}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "isActive",
                                  e.target.checked
                                )
                              }
                            />
                            <Label className="m-0">Is Active?</Label>
                          </div>
                          {/* Venue & Speaker */}
                        </CardBody>
                      </Card>
                    </TabPane>
                    <TabPane tabId="2">
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Event Cities</h5>
                            <Button color="primary" size="sm" onClick={addCity}>
                              + Add City
                            </Button>
                          </div>

                          <Nav tabs className="mb-3">
                            {formik.values.eventsCities.map((_, index) => (
                              <NavItem key={index}>
                                <NavLink
                                  active={activeTab === index}
                                  onClick={() => setActiveTab(index)}
                                  style={{ cursor: "pointer" }}
                                >
                                  City {index + 1}
                                  {formik.values.eventsCities.length > 1 && (
                                    <i
                                      className="mdi mdi-close ms-2"
                                      style={{ cursor: "pointer" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeCity(index);
                                      }}
                                    />
                                  )}
                                </NavLink>
                              </NavItem>
                            ))}
                          </Nav>

                          <TabContent activeTab={activeTab}>
                            {formik.values.eventsCities.map((city, index) => (
                              <TabPane tabId={index} key={index}>
                                <Row>
                                  <Col lg={4}>
                                    <Card>
                                      <CardBody className="text-center">
                                        {selectedCityMedia[index] ? (
                                          <img
                                            src={`${imageBaseApi}${selectedCityMedia[index].url}`}
                                            alt="City"
                                            className="img-fluid rounded"
                                            style={{ maxHeight: "200px" }}
                                          />
                                        ) : (
                                          <div className="py-5 text-muted">
                                            <i className="bi bi-image fs-1"></i>
                                            <p>No image selected</p>
                                          </div>
                                        )}
                                        <Button
                                          color="primary"
                                          size="sm"
                                          className="mt-3"
                                          onClick={() => openMediaModal(index)}
                                        >
                                          {selectedCityMedia[index]
                                            ? "Change"
                                            : "Select"}{" "}
                                          Image
                                        </Button>
                                      </CardBody>
                                    </Card>
                                  </Col>

                                  <Col lg={8}>
                                    <Row>
                                      <Col md={12}>
                                        <div className="mb-2">
                                          <Label>
                                            City{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </Label>
                                          <Input
                                            name={`eventsCities[${index}].city`}
                                            value={city.city}
                                            onChange={formik.handleChange}
                                            invalid={
                                              formik.touched.eventsCities?.[
                                                index
                                              ]?.city &&
                                              !!formik.errors.eventsCities?.[
                                                index
                                              ]?.city
                                            }
                                          />
                                        </div>
                                      </Col>
                                      <Col md={12}>
                                        <div className="mb-2">
                                          <Label>
                                            Date{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </Label>
                                          <Flatpickr
                                            className="form-control"
                                            options={{ dateFormat: "Y-m-d" }}
                                            value={city.eventDate}
                                            onChange={([date]) =>
                                              formik.setFieldValue(
                                                `eventsCities[${index}].eventDate`,
                                                date
                                              )
                                            }
                                          />
                                        </div>
                                      </Col>
                                      <Col md={12}>
                                        <div className="mb-2">
                                          <Label>
                                            Time Range{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </Label>

                                          <div className="d-flex gap-2">
                                            {/* Start Time */}
                                            <Flatpickr
                                              options={{
                                                enableTime: true,
                                                noCalendar: true,
                                                dateFormat: "H:i",
                                              }}
                                              value={
                                                city.timeRange
                                                  ? city.timeRange.split(
                                                      " - "
                                                    )[0]
                                                  : ""
                                              }
                                              placeholder="Start Time"
                                              onChange={(time) => {
                                                const start = time[0];
                                                const end = city.timeRange
                                                  ? city.timeRange.split(
                                                      " - "
                                                    )[1]
                                                  : "";
                                                formik.setFieldValue(
                                                  `eventsCities[${index}].timeRange`,
                                                  end
                                                    ? `${formatTime(
                                                        start
                                                      )} - ${end}`
                                                    : `${formatTime(start)} - `
                                                );
                                              }}
                                              className="form-control"
                                            />

                                            {/* End Time */}
                                            <Flatpickr
                                              options={{
                                                enableTime: true,
                                                noCalendar: true,
                                                dateFormat: "H:i",
                                              }}
                                              value={
                                                city.timeRange
                                                  ? city.timeRange.split(
                                                      " - "
                                                    )[1]
                                                  : ""
                                              }
                                              placeholder="End Time"
                                              onChange={(time) => {
                                                const end = time[0];
                                                const start = city.timeRange
                                                  ? city.timeRange.split(
                                                      " - "
                                                    )[0]
                                                  : "";
                                                formik.setFieldValue(
                                                  `eventsCities[${index}].timeRange`,
                                                  start
                                                    ? `${start} - ${formatTime(
                                                        end
                                                      )}`
                                                    : ` - ${formatTime(end)}`
                                                );
                                              }}
                                              className="form-control"
                                            />
                                          </div>
                                        </div>
                                      </Col>

                                      <Col md={12}>
                                        <div className="mb-2">
                                          <Label>
                                            Location{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </Label>
                                          <Input
                                            name={`eventsCities[${index}].location`}
                                            value={city.location}
                                            onChange={formik.handleChange}
                                          />
                                        </div>
                                      </Col>
                                      <Col md={12}>
                                        <div className="mb-2">
                                          <Label>
                                            Phone Number{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </Label>
                                          <Input
                                            name={`eventsCities[${index}].phoneNumber`}
                                            value={city.phoneNumber}
                                            onChange={formik.handleChange}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </TabPane>
                            ))}
                          </TabContent>
                        </CardBody>
                      </Card>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Banner Media */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Label>Banner Media</Label>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => openMediaModal("banner")}
                    >
                      +
                    </Button>
                  </div>
                  {selectedBannerMedia ? (
                    <>
                      <img
                        src={`${imageBaseApi}${selectedBannerMedia.url}`}
                        alt="Banner"
                        className="img-fluid rounded"
                        style={{ maxHeight: "200px" }}
                      />
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="rounded-pill px-3"
                          onClick={() => openMediaModal("thumbnail")}
                        >
                          Change
                        </Button>

                        <Button
                          size="sm"
                          color="danger"
                          outline
                          type="button"
                          className="rounded-pill px-3"
                          onClick={() => {
                            setSelectedBannerMedia(null);
                          }}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i>
                          Remove
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 border rounded text-center bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-3 text-muted">
                        No banner image selected
                      </h6>
                      <h6 className="mb-2 text-muted">2400 x 400</h6>

                      <Button
                        outline
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={() => openMediaModal("banner")}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose Banner
                        Image
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Thumbnail Media */}
              <Card className="mt-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Label>Thumbnail Media</Label>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => openMediaModal("thumbnail")}
                    >
                      +
                    </Button>
                  </div>
                  {selectedThumbnailMedia ? (
                    <>
                      <img
                        src={`${imageBaseApi}${selectedThumbnailMedia.url}`}
                        alt="Thumbnail"
                        className="img-fluid rounded"
                        style={{ maxHeight: "150px" }}
                      />
                      <div className="d-flex gap-2 mt-2">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="rounded-pill px-3"
                          onClick={() => openMediaModal("thumbnail")}
                        >
                          Change
                        </Button>

                        <Button
                          size="sm"
                          color="danger"
                          outline
                          type="button"
                          className="rounded-pill px-3"
                          onClick={() => {
                            setSelectedThumbnailMedia(null);
                          }}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i>
                          Remove
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center p-4 border rounded bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-2 text-muted">No media added</h6>
                      <h6 className="mb-2 text-muted">800 x 800</h6>

                      <Button
                        outline
                        size="sm"
                        color="secondary"
                        className="rounded-pill px-3"
                        onClick={() => openMediaModal("thumbnail")}
                      >
                        <i className="ri-image-add-line me-1"></i> Add Media
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Registration */}
              <Card className="mt-3">
                <CardHeader>Registration</CardHeader>
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <Label>Venue</Label>
                        <Input
                          name="venue"
                          value={formik.values.venue}
                          onChange={formik.handleChange}
                        />
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="mb-3">
                        <Label>Speaker</Label>
                        <Input
                          name="speaker"
                          value={formik.values.speaker}
                          onChange={formik.handleChange}
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className="mb-3 d-flex align-items-center gap-2">
                    <Input
                      type="checkbox"
                      checked={formik.values.registrationRequired}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "registrationRequired",
                          e.target.checked
                        )
                      }
                    />
                    <Label className="m-0">Registration Required</Label>
                  </div>

                  {formik.values.registrationRequired && (
                    <div className="mb-3">
                      <Label>Registration URL</Label>
                      <Input
                        name="registrationUrl"
                        value={formik.values.registrationUrl}
                        onChange={formik.handleChange}
                        invalid={
                          formik.touched.registrationUrl &&
                          !!formik.errors.registrationUrl
                        }
                      />
                      {formik.touched.registrationUrl &&
                        formik.errors.registrationUrl && (
                          <div className="text-danger mt-1">
                            {formik.errors.registrationUrl}
                          </div>
                        )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        {/* Media Modal */}
        <MediaModal
          isOpen={isMediaOpen}
          toggle={toggleMedia}
          mediaData={mediaData}
          selectedMedia={
            mediaSelectionType === "banner"
              ? selectedBannerMedia
              : mediaSelectionType === "thumbnail"
              ? selectedThumbnailMedia
              : typeof mediaSelectionType === "number"
              ? selectedCityMedia[mediaSelectionType]
              : null
          }
          setSelectedMedia={handleMediaSelect}
        />
      </div>
    </Container>
  );
};

export default EventForm;
